import { useEffect } from "react";
import { useTranslation } from "../../i18n";
import { useReducedMotion } from "../../hooks/useReducedMotion";

interface TimerFlashProps {
  /** Cleared by the parent once the flash has played. */
  onDone: () => void;
}

/** How long the whole effect runs, matched to the CSS below. */
const DURATION_MS = 2200;

/**
 * Plays once when the countdown reaches zero.
 *
 * A timebox ending is an attention cue, not a celebration — the point is that
 * everyone in the room notices at the same moment, so this is one expanding
 * ring, a brief warm tint and a label, then gone. It never blocks the board:
 * it ignores the pointer and removes itself.
 */
export function TimerFlash({ onDone }: TimerFlashProps) {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    // Still schedule the cleanup when motion is reduced, so the parent's
    // "flashing" state resets either way.
    const timeout = setTimeout(onDone, reducedMotion ? 0 : DURATION_MS);
    return () => clearTimeout(timeout);
  }, [onDone, reducedMotion]);

  // The timer text already switches to "Time's up" and changes colour, and the
  // change is announced, so there is a non-animated cue without this.
  if (reducedMotion) return null;

  return (
    <div
      aria-hidden="true"
      data-testid="timer-flash"
      className="pointer-events-none fixed inset-0 z-40 overflow-hidden"
    >
      <span
        style={{ animation: `timerWash ${DURATION_MS}ms ease-out forwards` }}
        className="absolute inset-0 bg-nm-accent/12"
      />

      <span
        style={{ animation: `timerRing ${DURATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1) forwards` }}
        className="absolute left-1/2 top-1/2 h-[38rem] w-[38rem] rounded-full border-2 border-nm-accent/50"
      />

      <span
        style={{ animation: `timerPill ${DURATION_MS}ms ease-out forwards` }}
        className="absolute left-1/2 top-1/2 whitespace-nowrap rounded-full bg-nm-accent px-6 py-2.5 text-lg font-bold text-white shadow-float"
      >
        {t("timer.finished")}
      </span>
    </div>
  );
}
